/**
 * <TutorStudentPickerModal> — « Sélectionner les élèves » d'une activité commune (template).
 *
 * La modale de choix de `GenerateActivityModal.js` (`forStudents`), ouverte d'emblée quand le
 * tuteur touche « Activité » dans « Mes élèves », tous ses élèves déjà cochés. Ses élèves, un par
 * ligne (`<PersonRow>` : l'avatar, le nom, la case ; cochée, la ligne prend la marque de
 * sélection) ; en pied, « Confirmer (N) », qui attend au moins un élève, puis « Tout
 * sélectionner », l'un sous l'autre (côte à côte, le second libellé passait à la ligne au
 * téléphone). Deux actions, la croix pour sortir.
 *
 * Contrôlée : la sélection arrive en props et chaque case la remonte.
 */
import React from "react"
import { Pressable } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { Checkbox } from "../../atoms/Checkbox"
import { Stack } from "../../atoms/containers/Stack"
import { CardList } from "../../molecules/CardList"
import { Modal } from "../../molecules/Modal"
import { PersonRow } from "../../molecules/PersonRow"
import { type UITutorStudent } from "../../../ports/primary/ui/types/tutorEleves"

export type TutorStudentPickerModalProps = {
    visible: boolean
    onClose: () => void
    students: UITutorStudent[]
    selectedIds: UITutorStudent["id"][]
    onToggle: (id: UITutorStudent["id"]) => void
    onSelectAll: () => void
    onConfirm: () => void
    testID?: string
}

export function TutorStudentPickerModal(props: TutorStudentPickerModalProps) {
    const { visible, onClose, students, selectedIds, onToggle, onSelectAll, onConfirm, testID } = props
    const count = selectedIds.length

    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title="Sélectionner les élèves"
            sizeToContent
            footer={
                <Stack gap="2">
                    <Button label={`Confirmer (${count})`} fullWidth disabled={count === 0} onPress={onConfirm} />
                    <Button label="Tout sélectionner" variant="outline" fullWidth onPress={onSelectAll} />
                </Stack>
            }
            testID={testID}
        >
            <CardList elevation="none" style={styles.list}>
                {students.map((student) => {
                    const checked = selectedIds.includes(student.id)
                    // Toute la ligne coche : la ligne de personne ne presse pas d'elle-même (elle
                    // porterait son chevron), c'est la case qui le dit.
                    return (
                        <Pressable
                            key={student.id}
                            onPress={() => onToggle(student.id)}
                            accessibilityRole="checkbox"
                            accessibilityState={{ checked }}
                            accessibilityLabel={student.name}
                        >
                            <PersonRow
                                name={student.name}
                                avatarUrl={student.avatarUrl}
                                selected={checked}
                                trailing={<Checkbox checked={checked} onChange={() => onToggle(student.id)} />}
                            />
                        </Pressable>
                    )
                })}
            </CardList>
        </Modal>
    )
}

const styles = StyleSheet.create((theme) => ({
    list: { borderWidth: 1, borderColor: theme.colors.border },
}))
